/**
 * RiskApplication Types for MEV-Med Frontend
 * Type definitions for risk application management across organizational levels
 *
 * @version 2025.2.0
 * @description Tipos TypeScript para gestao unificada de aplicacoes de riscos (Phase 7)
 */

import type { ExamType, ReportType } from './occupationalRisk';
import type { RiskMatrixType } from './riskMatrix';

// ============================================================================
// UNIFIED SCOPE TYPES (Phase 7)
// ============================================================================

export const RISK_APPLICATION_SCOPES = ['company', 'department', 'occupation', 'employee'] as const;
export type RiskApplicationScope = typeof RISK_APPLICATION_SCOPES[number];

// Note: `ExposureLevel` is defined as an enum further below (kept for backward
// compatibility with existing consumers). The unified types reuse it rather
// than duplicating as a const-derived union — see issue #246.

export interface RiskApplicationExamTypeRow {
  id: number;
  riskApplicationId: number;
  examType: ExamType;
}

export interface RiskApplicationReportRow {
  id: number;
  riskApplicationId: number;
  reportType: ReportType;
}

export interface RiskApplicationExistingMeasureRow {
  id: number;
  riskApplicationId: number;
  category: 'ADMINISTRATIVE' | 'ENGINEERING';
  measure: string;
}

export interface RiskApplicationExistingEpiRow {
  id: number;
  riskApplicationId: number;
  epiId: number;
  epi?: {
    id: number;
    nome: string;
    metodo: string;
    atenuacao?: string | null;
  };
}

/**
 * Os oito campos da avaliação de periculosidade (NR-16). Só se aplicam a risco com
 * agente perigoso — o servidor recusa preenchidos nos demais (#424).
 */
export interface HazardousMeasurementFields {
  storedQuantity?: number | null;
  storedQuantityUnit?: string | null;
  transportedQuantity?: number | null;
  transportedQuantityUnit?: string | null;
  riskArea?: string | null;
  storageType?: string | null;
  hazardousFraming?: boolean;
  hazardousAnnex?: string | null;
}

/**
 * O enquadramento de insalubridade (NR-15) e o grau aplicado. Só se aplicam a risco com
 * agente insalubre, e vêm em par — o servidor recusa um sem o outro (#357).
 */
export interface UnhealthyMeasurementFields {
  unhealthyFraming?: boolean;
  unhealthyDegreeApplied?: string | null;
}

export interface RiskApplicationMeasurementRow extends HazardousMeasurementFields, UnhealthyMeasurementFields {
  id: number;
  riskApplicationId: number;
  measurementEquipmentId?: number | null;
  equipment?: { id: number; name: string; serialNumber?: string | null };
  /** Nulo na periculosidade: a NR-16 enquadra a atividade em vez de medir (#424) */
  value: number | null; // DECIMAL vem como string do backend — Number()-normalizar ao ler
  unit?: string | null;
  measuredAt: string;   // 'YYYY-MM-DD'
  observations?: string | null;
}

export interface MeasurementInput extends HazardousMeasurementFields, UnhealthyMeasurementFields {
  measurementEquipmentId?: number | null;
  /** Nulo na periculosidade: a NR-16 enquadra a atividade em vez de medir (#424) */
  value: number | null;
  unit?: string | null;
  measuredAt: string;
  observations?: string | null;
}

export interface RiskApplication {
  id: number;
  riskId: number;
  scope: RiskApplicationScope;
  scopeId: number;
  exposureLevel: ExposureLevel;
  startDate: string;
  endDate?: string | null;
  observations?: string | null;
  riskMatrixType?: RiskMatrixType | null;
  riskProbability?: number | null;
  riskSeverity?: number | null;
  createdAt: string;
  updatedAt: string;
  risk?: {
    id: number;
    riskFactorName: string;
    risk?: string;
    hazard?: string;
    riskType?: string;
    riskMatrixType?: RiskMatrixType | null;
    riskProbability?: number | null;
    riskSeverity?: number | null;
    measurementUnit?: string | null;
    toleranceLimit?: string | null;
    /** Preenchido marca o risco como NR-16 — é o que decide o cartão de enquadramento */
    hazardousAgent?: string | null;
    /** Ficha do catálogo (#417): de onde a medição tira as opções de limite de tolerância */
    hazardousAgentId?: number | null;
    /** Preenchido marca o risco como NR-15 — é o que decide o cartão de insalubridade (#357) */
    unhealthyAgent?: string | null;
    /** Grau do catálogo: sugestão de valor inicial na medição, nunca imposição (#357) */
    unhealthyDegree?: string | null;
  };
  examTypes?: RiskApplicationExamTypeRow[];
  reportTypes?: RiskApplicationReportRow[];
  existingAdministrativeOther?: string | null;
  existingEngineeringOther?: string | null;
  proposedAdministrative?: string | null;
  proposedEngineering?: string | null;
  proposedEpi?: string | null;
  existingMeasures?: RiskApplicationExistingMeasureRow[];
  existingEpis?: RiskApplicationExistingEpiRow[];
  measurements?: RiskApplicationMeasurementRow[];
}

export interface RiskApplicationCreatePayload {
  riskId: number;
  scope: RiskApplicationScope;
  scopeId: number;
  exposureLevel?: ExposureLevel;
  startDate: string;
  endDate?: string | null;
  observations?: string | null;
  riskMatrixType?: RiskMatrixType | null;
  riskProbability?: number | null;
  riskSeverity?: number | null;
  examTypes?: ExamType[];
  reportTypes?: ReportType[];
  existingAdministrativeMeasures?: string[];
  existingEngineeringMeasures?: string[];
  existingAdministrativeOther?: string | null;
  existingEngineeringOther?: string | null;
  existingEpiIds?: number[];
  proposedAdministrative?: string | null;
  proposedEngineering?: string | null;
  proposedEpi?: string | null;
  measurements?: MeasurementInput[];
}

export interface RiskApplicationUpdatePayload {
  exposureLevel?: ExposureLevel;
  startDate?: string;
  endDate?: string | null;
  observations?: string | null;
  riskMatrixType?: RiskMatrixType | null;
  riskProbability?: number | null;
  riskSeverity?: number | null;
  examTypes?: ExamType[];
  reportTypes?: ReportType[];
  existingAdministrativeMeasures?: string[];
  existingEngineeringMeasures?: string[];
  existingAdministrativeOther?: string | null;
  existingEngineeringOther?: string | null;
  existingEpiIds?: number[];
  proposedAdministrative?: string | null;
  proposedEngineering?: string | null;
  proposedEpi?: string | null;
  measurements?: MeasurementInput[];
}

export interface RiskApplicationBulkPayload {
  riskIds: number[];
  scope: RiskApplicationScope;
  scopeId: number;
  exposureLevel?: ExposureLevel;
  startDate: string;
  endDate?: string | null;
  observations?: string | null;
}

export interface RiskApplicationBulkResponse {
  created: RiskApplication[];
  duplicates: number[];
}

/**
 * Aggregated risk entry returned by GET /risk-applications/employee/:employeeId.
 * Represents a deduplicated risk applied to an employee via any scope in the
 * organisational hierarchy (company → department → occupation → employee).
 * The most specific scope (employee > occupation > department > company) wins.
 */
export interface EmployeeAppliedRisk {
  riskId: number;
  riskName: string;
  scope: RiskApplicationScope;
  exposureLevel: string | null;
}

// Labels for unified types
export const EXPOSURE_LEVEL_LABELS: Record<ExposureLevel, string> = {
  LOW: 'Baixo',
  MEDIUM: 'Médio',
  HIGH: 'Alto',
  VERY_HIGH: 'Muito Alto',
};

export const SCOPE_LABELS: Record<RiskApplicationScope, string> = {
  company: 'Empresa',
  department: 'Setor',
  occupation: 'Cargo',
  employee: 'Funcionário',
};

// ============================================================================
// LEGACY ENUMS (kept for backward compatibility)
// ============================================================================

/**
 * Application levels in the organizational hierarchy
 */
export enum ApplicationLevel {
  COMPANY = 'company',
  DEPARTMENT = 'department',
  OCCUPATION = 'occupation',
  EMPLOYEE = 'employee',
}

/**
 * Exposure level for risk applications
 */
export enum ExposureLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  VERY_HIGH = 'VERY_HIGH',
}

// ============================================================================
// APPLICATION DATA INTERFACES
// ============================================================================

/**
 * Data for creating/updating a risk application
 * Used in apply and update operations
 */
export interface RiskApplicationData {
  exposureLevel?: ExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

/**
 * Request payload for applying a risk
 */
export interface ApplyRiskRequest {
  riskId: number;
  exposureLevel?: ExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

/**
 * Request payload for applying multiple risks
 */
export interface ApplyMultipleRisksRequest {
  riskIds: number[];
  exposureLevel?: ExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

/**
 * Request payload for updating an application
 */
export interface UpdateRiskApplicationRequest {
  exposureLevel?: ExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

// ============================================================================
// RISK WITH APPLICATION (Applied Risks)
// ============================================================================

/**
 * Risk with application details
 * Represents a risk that has been applied to a target (company, department, occupation, or employee)
 */
export interface RiskWithApplication {
  id: number;
  riskId: number;
  riskName: string;
  riskFactorName: string;
  hazard: string;
  exposureLevel: ExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
  isActive: boolean;
  isCritical: boolean;
  exposureDays: number;
  applicationLevel: ApplicationLevel;
  createdAt: string;
  updatedAt: string;
}

/**
 * Check if a risk application is active (no endDate or endDate in future)
 */
export function isRiskActive(risk: RiskWithApplication): boolean {
  return risk.isActive;
}

/**
 * Check if a risk is critical (HIGH or VERY_HIGH exposure)
 */
export function isRiskCritical(risk: RiskWithApplication): boolean {
  return risk.isCritical;
}

// ============================================================================
// AVAILABLE RISK (Not Yet Applied)
// ============================================================================

/**
 * Available risk for application
 * Represents a risk that can be applied to a target
 */
export interface AvailableRisk {
  id: number;
  risk: string;
  riskFactorName: string;
  hazard: string;
  riskType?: string | null;
}

// ============================================================================
// CRITICAL EXPOSURE ALERTS
// ============================================================================

/**
 * Critical exposure alert
 */
export interface CriticalExposure {
  applicationId: number;
  riskId: number;
  riskName: string;
  targetId: number;
  targetType: ApplicationLevel;
  targetName: string;
  exposureLevel: ExposureLevel;
  exposureDays: number;
  startDate: string;
}

/**
 * Critical exposure alerts response with summary
 */
export interface CriticalExposureAlerts {
  critical: CriticalExposure[];
  summary: {
    totalCritical: number;
    veryHighCount: number;
    highCount: number;
  };
}

// ============================================================================
// API RESPONSE INTERFACES
// ============================================================================

/**
 * Response for get risks endpoint (applied + available)
 */
export interface RisksForTargetResponse {
  applied: RiskWithApplication[];
  available: AvailableRisk[];
}

/**
 * Generic API success response
 */
export interface RiskApplicationSuccessResponse {
  message: string;
  data?: {
    riskId?: number;
    level?: string;
    targetId?: number;
    success?: number[];
    failed?: Array<{ riskId: number; error: string }>;
  };
}

/**
 * API error response
 */
export interface RiskApplicationErrorResponse {
  error: string;
  code: string;
}

// ============================================================================
// TARGET INFORMATION
// ============================================================================

/**
 * Target entity for application context
 */
export interface ApplicationTarget {
  id: number;
  name: string;
  level: ApplicationLevel;
  parentId?: number;
  parentName?: string;
}

/**
 * Hierarchical target with full path
 */
export interface ApplicationTargetWithPath extends ApplicationTarget {
  companyId: number;
  companyName: string;
  departmentId?: number;
  departmentName?: string;
  occupationId?: number;
  occupationName?: string;
}

// ============================================================================
// UI COMPONENT PROPS
// ============================================================================

/**
 * Props for DualTransferList component (risk version)
 */
export interface RiskTransferListProps {
  applied: RiskWithApplication[];
  available: AvailableRisk[];
  onApply: (riskIds: number[], data?: RiskApplicationData) => Promise<void>;
  onRemove: (riskIds: number[]) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Props for RiskApplicationModal (for setting exposure details)
 */
export interface RiskApplicationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: RiskApplicationData) => Promise<void>;
  risk: AvailableRisk | RiskWithApplication | null;
  mode: 'apply' | 'edit';
  loading?: boolean;
}

/**
 * Props for ExposureLevelBadge component
 */
export interface ExposureLevelBadgeProps {
  level: ExposureLevel;
  showLabel?: boolean;
}

/**
 * Props for CriticalAlertsTable component
 */
export interface CriticalAlertsTableProps {
  alerts: CriticalExposureAlerts;
  onAlertClick?: (alert: CriticalExposure) => void;
  loading?: boolean;
}

// ============================================================================
// FILTER INTERFACES
// ============================================================================

/**
 * Filters for risk applications list
 */
export interface RiskApplicationFilters {
  targetLevel?: ApplicationLevel;
  targetId?: number;
  exposureLevel?: ExposureLevel;
  isActive?: boolean;
  isCritical?: boolean;
  search?: string;
}

/**
 * Filters for critical alerts
 */
export interface CriticalAlertsFilters {
  companyId: number;
  targetType?: ApplicationLevel;
  exposureLevel?: ExposureLevel;
}

// ============================================================================
// LABELS AND CONSTANTS
// ============================================================================

/**
 * Labels for application levels (Portuguese)
 */
export const ApplicationLevelLabels: Record<ApplicationLevel, string> = {
  [ApplicationLevel.COMPANY]: 'Empresa',
  [ApplicationLevel.DEPARTMENT]: 'Setor',
  [ApplicationLevel.OCCUPATION]: 'Cargo',
  [ApplicationLevel.EMPLOYEE]: 'Funcionario',
};

/**
 * Labels for exposure levels (Portuguese)
 */
export const ExposureLevelLabels: Record<ExposureLevel, string> = {
  [ExposureLevel.LOW]: 'Baixo',
  [ExposureLevel.MEDIUM]: 'Medio',
  [ExposureLevel.HIGH]: 'Alto',
  [ExposureLevel.VERY_HIGH]: 'Muito Alto',
};

/**
 * Colors for exposure level badges
 */
export const ExposureLevelColors: Record<ExposureLevel, 'success' | 'info' | 'warning' | 'error'> = {
  [ExposureLevel.LOW]: 'success',
  [ExposureLevel.MEDIUM]: 'info',
  [ExposureLevel.HIGH]: 'warning',
  [ExposureLevel.VERY_HIGH]: 'error',
};

/**
 * Risk type labels (Portuguese)
 */
export const RiskTypeLabels: Record<string, string> = {
  PHYSICAL: 'Fisico',
  CHEMICAL: 'Quimico',
  BIOLOGICAL: 'Biologico',
  ERGONOMIC: 'Ergonomico',
  ACCIDENT: 'Acidente',
  OTHER: 'Outro',
};

/**
 * Risk type colors
 */
export const RiskTypeColors: Record<string, string> = {
  PHYSICAL: '#2196F3',
  CHEMICAL: '#9C27B0',
  BIOLOGICAL: '#4CAF50',
  ERGONOMIC: '#FF9800',
  ACCIDENT: '#F44336',
  OTHER: '#607D8B',
};

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Type guard for checking if a risk is applied
 */
export function isRiskWithApplication(
  risk: RiskWithApplication | AvailableRisk
): risk is RiskWithApplication {
  return 'applicationLevel' in risk && 'exposureLevel' in risk;
}

/**
 * Format exposure days for display
 */
export function formatExposureDays(days: number): string {
  if (days === 0) {
    return 'Hoje';
  }
  if (days === 1) {
    return '1 dia';
  }
  if (days < 30) {
    return `${days} dias`;
  }
  const months = Math.floor(days / 30);
  if (months === 1) {
    return '1 mes';
  }
  if (months < 12) {
    return `${months} meses`;
  }
  const years = Math.floor(months / 12);
  if (years === 1) {
    return '1 ano';
  }
  return `${years} anos`;
}

/**
 * Get exposure level from string (case insensitive)
 */
export function parseExposureLevel(level: string): ExposureLevel {
  const normalized = level.toUpperCase();
  if (normalized in ExposureLevel) {
    return normalized as ExposureLevel;
  }
  return ExposureLevel.MEDIUM;
}

/**
 * Sort risks by exposure level (most critical first)
 */
export function sortByExposureLevel(risks: RiskWithApplication[]): RiskWithApplication[] {
  const order: Record<ExposureLevel, number> = {
    [ExposureLevel.VERY_HIGH]: 0,
    [ExposureLevel.HIGH]: 1,
    [ExposureLevel.MEDIUM]: 2,
    [ExposureLevel.LOW]: 3,
  };

  return [...risks].sort((a, b) => {
    return order[a.exposureLevel] - order[b.exposureLevel];
  });
}

/**
 * Group risks by risk type
 */
export function groupByRiskType(
  risks: (RiskWithApplication | AvailableRisk)[]
): Record<string, (RiskWithApplication | AvailableRisk)[]> {
  const grouped: Record<string, (RiskWithApplication | AvailableRisk)[]> = {};

  for (const risk of risks) {
    const type = isRiskWithApplication(risk)
      ? risk.riskFactorName
      : risk.riskFactorName;

    if (!grouped[type]) {
      grouped[type] = [];
    }
    grouped[type].push(risk);
  }

  return grouped;
}
